import { appointmentActionSession } from './appointmentActionSession';

import type {
  AppointmentKind,
  AppointmentScope,
  BookingStatus,
  DecodedBrowserAppointment,
  DecodedBrowserAppointmentV2,
  LifecycleStatus,
  Meeting,
  PlatformAdminAppointment,
  Schedule,
  SeatType,
} from './types';
import type { Session } from '@/types';

export interface CalendarAppointmentProjection {
  actionCapabilities: {
    cancel: {
      allowed: boolean;
      initiatorRequired: boolean;
      nearTermConfirmationRequired: boolean;
      opsNoteRequired: boolean;
      reasonRequired: boolean;
      scopes: AppointmentScope[];
    };
    join: { allowed: boolean };
    reschedule: { allowed: boolean; scopes: AppointmentScope[] };
  };
  baselineSchedule: Schedule;
  bookingStatus: BookingStatus;
  calendarEventId: string | null;
  cancellation: { reason: string | null; requestedAt: string } | null;
  coach: { displayName: string; id: string };
  currentSchedule: Schedule;
  id: string;
  kind: AppointmentKind;
  lifecycleStatus: LifecycleStatus;
  meeting: Meeting;
  member: { displayName: string; id: string };
  seatType: SeatType | null;
  seriesId: string | null;
  seriesMutationVersion: number;
  version: number;
}

export interface ParticipantCalendarAppointmentActionRecord {
  authoritative: DecodedBrowserAppointment;
  railsSession?: Session;
  session: Session;
  source: 'participant-appointment';
  view: CalendarAppointmentProjection;
}

export interface ParticipantCalendarAppointmentActionRecordV2 {
  authoritative: DecodedBrowserAppointmentV2;
  railsSession?: Session;
  session: Session;
  source: 'participant-appointment';
  view: CalendarAppointmentProjection;
}

export interface PlatformAdminCalendarAppointmentActionRecord {
  authoritative: PlatformAdminAppointment;
  railsSession?: Session;
  session: Session;
  source: 'platform-admin-appointment';
  view: CalendarAppointmentProjection;
}

export type CalendarAppointmentActionRecord =
  | ParticipantCalendarAppointmentActionRecord
  | ParticipantCalendarAppointmentActionRecordV2
  | PlatformAdminCalendarAppointmentActionRecord;

export type CalendarActionSubject =
  { session: Session; source: 'session' } | CalendarAppointmentActionRecord;

const participantProjection = (
  appointment: DecodedBrowserAppointment | DecodedBrowserAppointmentV2,
  calendarEventId: string | null,
): CalendarAppointmentProjection => ({
  actionCapabilities: {
    cancel: {
      ...appointment.actionCapabilities.cancel,
      initiatorRequired: false,
      nearTermConfirmationRequired: false,
      opsNoteRequired: false,
    },
    join: appointment.actionCapabilities.join,
    reschedule: appointment.actionCapabilities.reschedule,
  },
  baselineSchedule: appointment.baselineSchedule,
  bookingStatus: appointment.bookingStatus,
  calendarEventId,
  cancellation: appointment.cancellation
    ? {
        reason: appointment.cancellation.reason,
        requestedAt: appointment.cancellation.requestedAt,
      }
    : null,
  coach: {
    displayName: appointment.expectedIdentities.coach.displayName,
    id: appointment.expectedIdentities.coach.personId,
  },
  currentSchedule: appointment.currentSchedule,
  id: appointment.id,
  kind: appointment.kind,
  lifecycleStatus: appointment.lifecycleStatus,
  meeting: appointment.meeting,
  member: {
    displayName: appointment.expectedIdentities.member.displayName,
    id: appointment.expectedIdentities.member.personId,
  },
  seatType: appointment.seatType,
  seriesId: appointment.seriesId,
  seriesMutationVersion: appointment.seriesMutationVersion,
  version: appointment.version,
});

const platformAdminProjection = (
  appointment: PlatformAdminAppointment,
): CalendarAppointmentProjection => ({
  actionCapabilities: appointment.actionCapabilities,
  baselineSchedule: appointment.baselineSchedule,
  bookingStatus: appointment.bookingStatus,
  calendarEventId: appointment.calendarEventId,
  cancellation: appointment.cancellation
    ? {
        reason: appointment.cancellation.reason,
        requestedAt: appointment.cancellation.requestedAt,
      }
    : null,
  coach: appointment.coach,
  currentSchedule: appointment.currentSchedule,
  id: appointment.id,
  kind: appointment.kind,
  lifecycleStatus: appointment.lifecycleStatus,
  meeting: appointment.meeting,
  member: appointment.member,
  seatType: appointment.seatType,
  seriesId: appointment.seriesId,
  seriesMutationVersion: appointment.seriesMutationVersion,
  version: appointment.version,
});

export const participantCalendarAppointmentRecord = (
  appointment: DecodedBrowserAppointment,
): ParticipantCalendarAppointmentActionRecord => {
  const view = participantProjection(appointment, null);
  const session = appointmentActionSession(view);
  const cancellationRole = appointment.cancellation?.actor.role;
  let cancelledBy: Session['cancelledBy'];
  if (cancellationRole === 'coach' || cancellationRole === 'member') {
    cancelledBy = cancellationRole;
  } else if (appointment.cancellation) {
    cancelledBy = 'system';
  }
  return {
    authoritative: appointment,
    session: {
      ...session,
      cancelledBy,
      coach: { ...session.coach, id: '' },
      user: { ...session.user, id: '' },
    },
    source: 'participant-appointment',
    view,
  };
};

export const participantCalendarAppointmentRecordV2 = (
  appointment: DecodedBrowserAppointmentV2,
): ParticipantCalendarAppointmentActionRecordV2 => {
  const view = participantProjection(appointment, appointment.calendarEventId);
  const session = appointmentActionSession(view);
  const cancellationRole = appointment.cancellation?.actor.role;
  let cancelledBy: Session['cancelledBy'];
  if (cancellationRole === 'coach' || cancellationRole === 'member') {
    cancelledBy = cancellationRole;
  } else if (appointment.cancellation) {
    cancelledBy = 'system';
  }
  return {
    authoritative: appointment,
    session: {
      ...session,
      cancelledBy,
      coach: { ...session.coach, id: '' },
      user: { ...session.user, id: '' },
    },
    source: 'participant-appointment',
    view,
  };
};

export const platformAdminCalendarAppointmentRecord = (
  appointment: PlatformAdminAppointment,
): PlatformAdminCalendarAppointmentActionRecord => {
  const view = platformAdminProjection(appointment);
  const session = appointmentActionSession(view);
  const initiator = appointment.cancellation?.initiator;
  let cancelledBy: Session['cancelledBy'];
  if (initiator === 'coach' || initiator === 'member') {
    cancelledBy = initiator;
  } else if (initiator === 'admin') {
    cancelledBy = 'system';
  }
  return {
    authoritative: appointment,
    session: {
      ...session,
      cancelledBy,
    },
    source: 'platform-admin-appointment',
    view,
  };
};
